'use strict'

// Compact horizontal app-launcher row. Icon on the left, title +
// caption on the right. Lives in the SearchAppsMenu dropdown since
// 2026-06-06 (the home AppsWidget card it used to stack inside was
// removed). Opens the app inside the home AppWindow (PageSlot) when
// possible — plain routing otherwise — via el.call('openAppPage').
//
// Per-row state (fed by SEARCH_APPS in functions/searchApps.js via
// childrenAs: 'state'):
//   { title, caption, icon, route, appKey?, captionAppKey? }
// `appKey` is an installed app's record key (workspaceConfigs.js stamps it on
// the app's one launcher tile) or an App Store row's own key.
// `captionAppKey` rides only on an App Store row (functions/search/
// searchAppResults.js): its caption is that record's one line.
export const AppListItem = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  gap: 'A',
  align: 'center',
  round: 'radiusCard',
  padding: 'Z A',
  textAlign: 'left',
  cursor: 'pointer',
  color: 'title',
  background: 'transparent',
  border: 'none',
  width: '100%',
  // No transition — this row renders inside the SearchAppsMenu dropdown
  // (a MenuPanel-family surface): the runtime re-applies background per
  // tick there, which restarts a CSS transition every frame (§7.7 framework
  // constraint). `overlayStrong` is a near-opaque sheet wash, not a state
  // fill (§4.1) — the hover wash is the shared `hover` token (§4.3).
  ':hover': { background: 'hover' },

  onClick: (e, el, s) => {
    if (!s.route) return
    el.call('closeDropdown', 'searchApps')
    el.call('openAppPage', s.route)
  },

  IconTile: {
    width: 'C',
    height: 'C',
    minWidth: 'C',
    round: 'radiusControl',
    align: 'center center',
    background: 'overlay',
    color: 'title',
    Icon: { name: (el, s) => s.icon, boxSize: 'A' }
  },

  Body: {
    flow: 'y',
    gap: 'V',
    flex: '1',
    minWidth: '0',

    // Primary text: wraps, never cut (overflow law §3b rule 8).
    Title: {
      tag: 'span',
      fontSize: 'A',
      fontWeight: '700',
      color: 'title',
      overflowWrap: 'anywhere',
      // i18n — resolved through THE store-copy resolver
      // (shared/functions/storeItemText.js), the call the apps launcher's own
      // tile (SearchAppsMenu) and the navbar tab make, so an app has one name
      // on every surface (Nika 2026-10-23: "app names = what a person sees in
      // the workspace App Store"). Its source order serves every row kind with
      // no branch here:
      //   • an INSTALLED APP's tile and an App Store row carry `appKey` and
      //     read the store's name ("Insights", not the manifest's "Analytics");
      //   • a HOUSE tile's title is a polyglot key and resolves at the
      //     resolver's last source, which is polyglot;
      //   • a customer's own words resolve nothing and pass through unchanged.
      text: (el, s) => el.call('storeItemText', { key: s.appKey, name: s.title || '' }, 'name')
    },

    Caption: {
      tag: 'p',
      margin: '0',
      fontSize: 'Y',
      lineHeight: '1.4',
      color: 'caption',
      // Secondary meta: one line, the whole caption on hover (rule 8).
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      // i18n — a shipped caption is a polyglot key, an app's or a customer's
      // own caption is their words (polyglot returns them unchanged). An App
      // Store row's caption is the RECORD's one line, English only, so the row
      // names its record (`captionAppKey`) and the caption reads THE
      // store-copy resolver — the App Store card's own call: the store's line
      // in the session language (lane DESC, gap 3). Not `appKey`: an
      // installed app's tile carries that for its name, with its own caption.
      title: (el, s) =>
        s.captionAppKey
          ? el.call(
              'storeItemText',
              { key: s.captionAppKey, description: s.caption || '' },
              'description'
            )
          : el.call('polyglot', s.caption || ''),
      text: (el, s) =>
        s.captionAppKey
          ? el.call(
              'storeItemText',
              { key: s.captionAppKey, description: s.caption || '' },
              'description'
            )
          : el.call('polyglot', s.caption || '')
    }
  }
}
